import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { UseOutletContextPos } from './Pos';
import { StateLanguage } from '../../../languages/config/StateLanguage';
import { PosCashierDA } from '../../../services/admin/pos.cashier.service';
import { StorageCenterDA } from '../../../services/admin/storage.center.service';
import { CompanyPosCashiers, PosCashier } from '../../../models/pos.model';
import { Error } from '../../../models/error.model';
import { Message } from '../../../models/message.model';
import { GlobalSwal } from '../../../styles/swal.style';
import { getError, getMessage } from '../../../languages/translations/response';
import { errorWebHandler } from '../../../scripts/error.web.handler.script';
import { modalShow } from '../../../utils/modal.util';

import AppPreloader from '../../../components/Preloader';
import background from '../../../assets/themes/images/question.svg';
import EmptyState from '../../../components/element/EmptyState';
import AppModalPosCashierUpdate from '../../../components/modal/ModalPosCashierUpdate';
import { formatContact } from '../../../scripts/format.value.script';
import { TreasuryAccount } from '../../../models/treasury.account.model';
import { StorageCenter } from '../../../models/storage.center.model';

export interface PosListPageProps {};

let errorResponse: Error, messageResponse: Message, companyPosCashiersResponse: CompanyPosCashiers;

const PosListPage: React.FunctionComponent<PosListPageProps> = props => {
  const {setRoute, companyForUser, treasuryAccountsForCompany} = UseOutletContextPos()
  const {lang} = StateLanguage()

  const [mounted, setMounted] = useState(false)
  const [posCashiers, setPosCashiers] = useState<PosCashier[] | null>(null)
  const [posCashier, setPosCashier] = useState<PosCashier | null>(null)
  const [localStorageCenters, setLocalStorageCenters] = useState<StorageCenter[]>([])

  const resolveTreasuryAccount = (idOrObj: any): TreasuryAccount | undefined => {
    if (!idOrObj) return undefined
    if (typeof idOrObj === 'object' && idOrObj.id) return idOrObj
    if (typeof idOrObj === 'string' && treasuryAccountsForCompany) return treasuryAccountsForCompany.find(a => a.id === idOrObj)
    return undefined
  }

  const resolveStorageCenter = (item: any): StorageCenter | undefined => {
    const ref = item.storage_center ?? item.id_storage_center
    if (!ref) return undefined
    if (typeof ref === 'object' && ref.name) return ref
    if (typeof ref === 'string') return localStorageCenters.find(s => s.id === ref)
    return undefined
  }

  const resolveCashAccounts = (item: any): {id: string, currency: string, name: string}[] => {
    const accounts = item.treasury_cash_accounts ?? []
    return accounts.map((entry: any) => {
      const resolved = resolveTreasuryAccount(entry)
      return resolved ? {id: resolved.id, currency: resolved.currency, name: resolved.name} : null
    }).filter(Boolean)
  }

  const resolveCardAccount = (item: any): TreasuryAccount | undefined => {
    return resolveTreasuryAccount(item.treasury_card)
  }

  const resolveCustomerName = (item: any): string => {
    const ref = (item as any).default_customer ?? item.customer_default ?? item.id_customer
    if (!ref) return '-'
    if (typeof ref === 'object') {
      if (ref.name) return ref.name
      if (ref.data) return formatContact(ref)
      return ref.id ?? '-'
    }
    return '-'
  }

  const loadPosCashiers = async (id_company: string) => {
    await PosCashierDA.getCashiers(id_company).then( (response) => {
      if (response.status === 200) {
        companyPosCashiersResponse = response.data
        setPosCashiers(companyPosCashiersResponse.cashiers ?? companyPosCashiersResponse.items ?? [])
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  const submitPosCashierDelete = async (pos: PosCashier) => {
    if (companyForUser) {
      await GlobalSwal.fire({
        title: lang.labels.youWantToDeleteThisPOS,
        text: lang.labels.youWillNotBeAbleToUndo,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: lang.labels.yesDelete,
        cancelButtonText: lang.labels.noCancel
      }).then(async (result) => {
        if (result.isConfirmed) {
          await PosCashierDA.deleteCashier(companyForUser.id, pos.id).then( (response) => {
            if (response.status === 200) {
              messageResponse = response.data
              loadPosCashiers(companyForUser.id)

              GlobalSwal.fire({
                title: getMessage(messageResponse.message, lang.code),
                text: lang.labels.actionCompletedReturningToPage,
                icon: 'success',
                showConfirmButton: false,
                timer: 2200
              })
            } else {
              errorResponse = response.data

              GlobalSwal.fire({
                title: getError(errorResponse.code, lang.code, errorResponse.message),
                text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
                icon: 'error',
                confirmButtonText: lang.labels.okGotIt
              })
            }
          }).catch( (error) => {
            errorWebHandler(lang, error)
          })
        }
      })
    }
  }

  const executeShowModalPosCashierUpdate = (item: PosCashier) => {
    setPosCashier(item)
    modalShow('modal-pos-cashier-update')
  }

  useEffect( () => {
    if (companyForUser) {
      setPosCashiers([])
      loadPosCashiers(companyForUser.id)

      StorageCenterDA.getStorageCenters(companyForUser.id, '', 'name', 'asc').then(response => {
        if (response.status === 200) {
          const data = response.data?.data ?? response.data
          setLocalStorageCenters(data.items ?? data.storage_centers ?? data ?? [])
        }
      }).catch(() => {})
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser])

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.pos, branch: lang.labels.listPOS}, company: true})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { companyForUser && posCashiers
        ?
        <>
        { posCashiers.length > 0
          ?
          <>
          <div className="row row-cols-1 row-cols-md-2 row-cols-xl-3 g-2">
            { posCashiers.map (( (item, index) => { return (
              <div key={index} className="col">
                <div className="card card-flush h-md-100">
                  <div className="card-header flex-nowrap">
                    <div className="card-title">
                      <h4>{item.name}</h4>
                    </div>
                  </div>
                  <div className="card-body py-0">
                    <div className="d-flex flex-column">
                      <div className="row mb-2">
                        <label className="text-muted fw-bold fs-8">{lang.labels.code}</label>
                        <label className="fw-bolder text-uppercase">{item.code}</label>
                      </div>
                      <div className="row mb-2">
                        <div>
                          { (item.stock_enabled !== false)
                            ? <span className="badge badge-light-info">{lang.labels.stockManaged}{resolveStorageCenter(item) ? ` — ${resolveStorageCenter(item)!.name}` : ''}</span>
                            : <span className="badge badge-light-secondary">{lang.labels.withoutStockManagement}</span>
                          }
                        </div>
                      </div>
                      <div className="row mb-2">
                        <label className="text-muted fw-bold fs-8">{lang.labels.cash}</label>
                        <div>
                          { resolveCashAccounts(item).length > 0
                            ? resolveCashAccounts(item).map((account) => (
                                <span key={account.id} className="badge badge-light-primary me-1 mb-1">
                                  {account.currency} - {account.name}
                                </span>
                              ))
                            : <label className="fw-bolder text-uppercase text-muted">-</label>
                          }
                        </div>
                      </div>
                      <div className="row mb-2">
                        <label className="text-muted fw-bold fs-8">{lang.labels.card}</label>
                        <label className="fw-bolder text-uppercase">{resolveCardAccount(item)?.name ?? '-'}</label>
                      </div>
                      { item.default_cash_currency &&
                        <div className="row mb-2">
                          <label className="text-muted fw-bold fs-8">{lang.labels.defaultCurrency ?? 'Default currency'}</label>
                          <label className="fw-bolder text-uppercase">{item.default_cash_currency}</label>
                        </div>
                      }
                      <div className="row">
                        <label className="text-muted fw-bold fs-8">{lang.labels.defaultCustomerSale}</label>
                        <label className="fw-bolder text-uppercase">{resolveCustomerName(item)}</label>
                      </div>
                    </div>
                  </div>
                  <div className="card-footer d-flex justify-content-center py-5">
                    <Link to={"/main/pos/view/" + item.id} className="btn btn-sm btn-light-primary mx-1">{lang.labels.view}</Link>
                    <button className="btn btn-sm btn-light-primary mx-1"  type="button" onClick={() => executeShowModalPosCashierUpdate(item)}>{lang.labels.edit}</button>
                    <button className="btn btn-sm btn-light-primary mx-1" type="button" onClick={() => submitPosCashierDelete(item)}>{lang.labels.remove}</button>
                  </div>
                </div>
              </div>
            )}))}
          </div>
          </>
          :
          <EmptyState illustration={background} illustrationAlt="" title={lang.labels.noRecordsFound} description={lang.labels.startCreatingNewPOSForYourCompany} />
        }
        </>
        :
        <AppPreloader></AppPreloader>
      }
      <AppModalPosCashierUpdate companyForUser={companyForUser} treasuryAccountsForCompany={treasuryAccountsForCompany} storageCentersForCompany={localStorageCenters} posCashier={posCashier} posCashiers={posCashiers} setPosCashier={setPosCashier} setPosCashiers={setPosCashiers}></AppModalPosCashierUpdate>
    </div>
  )
};

export default PosListPage;
